/**
 * authLock — Supabase 인증 클라이언트가 쓰는 탭 간 잠금(Web Locks) 래퍼.
 *
 * 배경(2026-09-05, test-rhoonart 모바일 무한 로딩): supabase-js 는 세션을 읽고 갱신할 때
 * `navigator.locks` 로 같은 origin 의 탭끼리 `lock:<storageKey>` 잠금을 잡는다. 기본 구현은
 * 대기 시간 제한이 없어서(acquireTimeout = -1), 모바일 브라우저가 백그라운드 탭을 **얼린 채**
 * 그 탭이 잠금을 쥐고 있으면 새로 연 탭은 `getSession()` 에서 영원히 기다린다 — 화면은 스피너,
 * 서버 요청은 0건. 이 래퍼는 잠금을 `LOCK_WAIT_MS` 안에 못 잡으면 얼어붙은 탭에서 잠금을
 * 빼앗아(`steal: true`) 진행한다. 평소(잠금이 바로 잡히는 경우)에는 기본 구현과 동작이 같다.
 *
 * supabase-js 가 부르는 규약(@supabase/auth-js lib/locks.ts):
 *   lock(name, acquireTimeout, fn)
 *   - acquireTimeout < 0 : 제한 없음 → 여기서는 LOCK_WAIT_MS 로 바꾸고, 초과 시 steal
 *   - acquireTimeout = 0 : 즉시 못 잡으면 `isAcquireTimeout = true` 인 오류 (자동 갱신 틱이 이걸 보고 건너뜀)
 *   - acquireTimeout > 0 : 그 시간만 기다리고 초과 시 `isAcquireTimeout = true` 오류 (steal 없음)
 *   - `navigator.locks` 가 없는 환경(구형 WebView·일부 iframe) : 탭 안에서만 직렬화 (auth-js 의 processLock 과 같은 역할)
 */

export const LOCK_WAIT_MS = 5000;

function acquireTimeoutError(name, ms) {
  const err = new Error(`인증 잠금 "${name}" 을(를) ${ms}ms 안에 잡지 못했습니다`);
  err.isAcquireTimeout = true;
  err.name = "AuthLockAcquireTimeoutError";
  return err;
}

// navigator.locks 가 없을 때의 폴백 — 같은 이름은 한 번에 하나만 실행(탭 안 직렬화)
const inProcessChains = new Map();
async function inProcessLock(name, fn) {
  const prev = inProcessChains.get(name) || Promise.resolve();
  let release;
  const gate = new Promise((r) => (release = r));
  inProcessChains.set(name, prev.then(() => gate));
  try {
    await prev;
    return await fn();
  } finally {
    release();
    if (inProcessChains.get(name) === gate) inProcessChains.delete(name);
  }
}

function hasNavigatorLocks(nav) {
  return Boolean(nav && nav.locks && typeof nav.locks.request === "function");
}

/**
 * @param {string} name
 * @param {number} acquireTimeout
 * @param {() => Promise<any>} fn
 * @param {{ navigator?: Navigator, waitMs?: number, warn?: (msg: string) => void }} [opts] — 테스트용 주입
 */
export async function boundedNavigatorLock(name, acquireTimeout, fn, opts = {}) {
  const nav = opts.navigator ?? (typeof navigator !== "undefined" ? navigator : undefined);
  const waitMs = opts.waitMs ?? LOCK_WAIT_MS;
  const warn = opts.warn ?? ((msg) => console.warn(msg));

  if (!hasNavigatorLocks(nav)) return inProcessLock(name, fn);

  if (acquireTimeout === 0) {
    return nav.locks.request(name, { mode: "exclusive", ifAvailable: true }, async (lock) => {
      if (!lock) throw acquireTimeoutError(name, 0);
      return fn();
    });
  }

  const unbounded = acquireTimeout < 0;
  const timeoutMs = unbounded ? waitMs : acquireTimeout;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await nav.locks.request(name, { mode: "exclusive", signal: controller.signal }, async (lock) => {
      // 스펙을 안 따르는 브라우저가 null 을 주면 auth-js 와 같이 그냥 실행한다
      return fn();
    });
  } catch (e) {
    const timedOut = controller.signal.aborted || e?.name === "AbortError";
    if (!timedOut) throw e;
    if (!unbounded) throw acquireTimeoutError(name, timeoutMs);
    warn(
      `[supabase] 인증 잠금 "${name}" 을(를) ${timeoutMs}ms 동안 잡지 못했습니다 — 다른(얼어붙은) 탭이 쥐고 있어 빼앗아 진행합니다.`
    );
    return nav.locks.request(name, { mode: "exclusive", steal: true }, async () => fn());
  } finally {
    clearTimeout(timer);
  }
}

export default boundedNavigatorLock;
